import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../Providers/LanguageContext';
import styles from './PrivacyPolicy.module.css';
import { playUISound } from '../../utils/UISoundEffects';

export function PrivacyPolicy() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  return (
    <div className={styles.privacyContainer}>
      <button
        className={styles.backButton}
        onClick={() => { playUISound(); navigate(-1); }}
        aria-label={t('common.back')}
      >
        ← {t('common.back')}
      </button>
      <div className={styles.header}>
        <h1>{t('privacy.title')}</h1>
        <p className={styles.lastUpdated}>{t('privacy.lastUpdated')}: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <div className={styles.content}>
        {/* Introduction */}
        <section className={styles.section}>
          <h2>{t('privacy.introduction.title')}</h2>
          <p>{t('privacy.introduction.p1')}</p>
          <p>{t('privacy.introduction.p2')}</p>
          <p>
            <strong>{t('privacy.introduction.contact')}</strong> {t('common.contactEmail')}
          </p>
        </section>

        {/* Information We Collect */}
        <section className={styles.section}>
          <h2>{t('privacy.infoCollect.title')}</h2>

          <h3>{t('privacy.infoCollect.accountInfo.title')}</h3>
          <ul>
            <li>{t('privacy.infoCollect.accountInfo.item1')}</li>
            <li>{t('privacy.infoCollect.accountInfo.item2')}</li>
            <li>{t('privacy.infoCollect.accountInfo.item3')}</li>
          </ul>

          <h3>{t('privacy.infoCollect.gameData.title')}</h3>
          <ul>
            <li>{t('privacy.infoCollect.gameData.item1')}</li>
            <li>{t('privacy.infoCollect.gameData.item2')}</li>
            <li>{t('privacy.infoCollect.gameData.item3')}</li>
            <li>{t('privacy.infoCollect.gameData.item4')}</li>
          </ul>

          <h3>{t('privacy.infoCollect.technicalData.title')}</h3>
          <ul>
            <li>{t('privacy.infoCollect.technicalData.item1')}</li>
            <li>{t('privacy.infoCollect.technicalData.item2')}</li>
            <li>{t('privacy.infoCollect.technicalData.item3')}</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section className={styles.section}>
          <h2>{t('privacy.children.title')}</h2>
          <p>{t('privacy.children.p1')}</p>
          <p>{t('privacy.children.p2')}</p>
          <p>{t('privacy.children.p3')}</p>
          <p>{t('privacy.children.p4')}</p>
          <p>
            <strong>{t('privacy.children.parentalRights')}</strong>
          </p>
          <ul>
            <li>{t('privacy.children.right1')}</li>
            <li>{t('privacy.children.right2')}</li>
            <li>{t('privacy.children.right3')}</li>
          </ul>
          <p>{t('privacy.children.contactUs')}</p>
        </section>

        {/* How We Use Your Information */}
        <section className={styles.section}>
          <h2>{t('privacy.howWeUse.title')}</h2>
          <ul>
            <li>{t('privacy.howWeUse.item1')}</li>
            <li>{t('privacy.howWeUse.item2')}</li>
            <li>{t('privacy.howWeUse.item3')}</li>
            <li>{t('privacy.howWeUse.item4')}</li>
            <li>{t('privacy.howWeUse.item5')}</li>
            <li>{t('privacy.howWeUse.item6')}</li>
          </ul>
        </section>

        {/* Third-Party Services */}
        <section className={styles.section}>
          <h2>{t('privacy.thirdParty.title')}</h2>

          <h3>{t('privacy.thirdParty.firebase.title')}</h3>
          <p>{t('privacy.thirdParty.firebase.description')}</p>
          <p>
            <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">
              {t('privacy.thirdParty.viewPolicy')}
            </a>
          </p>

          <h3>{t('privacy.thirdParty.stripe.title')}</h3>
          <p>{t('privacy.thirdParty.stripe.description')}</p>
          <p>
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              {t('privacy.thirdParty.viewPolicy')}
            </a>
          </p>

          <h3>{t('privacy.thirdParty.netlify.title')}</h3>
          <p>{t('privacy.thirdParty.netlify.description')}</p>
          <p>
            <a href="https://www.netlify.com/privacy/" target="_blank" rel="noopener noreferrer">
              {t('privacy.thirdParty.viewPolicy')}
            </a>
          </p>

          <h3>{t('privacy.thirdParty.adsense.title')}</h3>
          <p>{t('privacy.thirdParty.adsense.description')}</p>
          <p>
            <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
              {t('privacy.thirdParty.viewPolicy')}
            </a>
          </p>
        </section>

        {/* Cookies and Local Storage */}
        <section className={styles.section}>
          <h2>{t('privacy.cookies.title')}</h2>
          <p>{t('privacy.cookies.description')}</p>
          <ul>
            <li>{t('privacy.cookies.item1')}</li>
            <li>{t('privacy.cookies.item2')}</li>
            <li>{t('privacy.cookies.item3')}</li>
            <li>{t('privacy.cookies.item4')}</li>
          </ul>
        </section>

        {/* Cookie Consent & Control */}
        <section className={styles.section}>
          <h2>{t('privacy.cookieConsent.title')}</h2>
          <p>{t('privacy.cookieConsent.p1')}</p>

          <h3>{t('privacy.cookieConsent.categories.title')}</h3>
          <ul>
            <li>
              <strong>{t('privacy.cookieConsent.categories.necessary.title')}</strong> -
              {t('privacy.cookieConsent.categories.necessary.description')}
            </li>
            <li>
              <strong>{t('privacy.cookieConsent.categories.analytics.title')}</strong> -
              {t('privacy.cookieConsent.categories.analytics.description')}
            </li>
            <li>
              <strong>{t('privacy.cookieConsent.categories.advertising.title')}</strong> -
              {t('privacy.cookieConsent.categories.advertising.description')}
            </li>
          </ul>

          <h3>{t('privacy.cookieConsent.control.title')}</h3>
          <p>{t('privacy.cookieConsent.control.p1')}</p>
          <ul>
            <li>{t('privacy.cookieConsent.control.method1')}</li>
            <li>{t('privacy.cookieConsent.control.method2')}</li>
            <li>{t('privacy.cookieConsent.control.method3')}</li>
          </ul>

          <h3>{t('privacy.cookieConsent.optOut.title')}</h3>
          <p>{t('privacy.cookieConsent.optOut.p1')}</p>
          <ul className={styles.linksList}>
            <li>
              <a href="https://support.google.com/ads/answer/2662856" target="_blank" rel="noopener noreferrer">
                {t('privacy.cookieConsent.optOut.googleAds')}
              </a>
            </li>
            <li>
              <a href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">
                {t('privacy.cookieConsent.optOut.daa')}
              </a>
            </li>
            <li>
              <a href="https://youronlinechoices.eu/" target="_blank" rel="noopener noreferrer">
                {t('privacy.cookieConsent.optOut.eaa')}
              </a>
            </li>
          </ul>

          <p>{t('privacy.cookieConsent.moreInfo')} <a href="/cookies" className={styles.link}>{t('privacy.cookieConsent.cookiePolicyLink')}</a>.</p>
        </section>

        {/* Data Security */}
        <section className={styles.section}>
          <h2>{t('privacy.security.title')}</h2>
          <p>{t('privacy.security.p1')}</p>
          <ul>
            <li>{t('privacy.security.item1')}</li>
            <li>{t('privacy.security.item2')}</li>
            <li>{t('privacy.security.item3')}</li>
          </ul>
          <p>{t('privacy.security.p2')}</p>
        </section>

        {/* Data Retention */}
        <section className={styles.section}>
          <h2>{t('privacy.retention.title')}</h2>
          <p>{t('privacy.retention.p1')}</p>
          <ul>
            <li>{t('privacy.retention.item1')}</li>
            <li>{t('privacy.retention.item2')}</li>
          </ul>
        </section>

        {/* Your Rights */}
        <section className={styles.section}>
          <h2>{t('privacy.rights.title')}</h2>
          <p>{t('privacy.rights.description')}</p>
          <ul>
            <li><strong>{t('privacy.rights.access.title')}</strong> {t('privacy.rights.access.description')}</li>
            <li><strong>{t('privacy.rights.correction.title')}</strong> {t('privacy.rights.correction.description')}</li>
            <li><strong>{t('privacy.rights.deletion.title')}</strong> {t('privacy.rights.deletion.description')}</li>
            <li><strong>{t('privacy.rights.portability.title')}</strong> {t('privacy.rights.portability.description')}</li>
            <li><strong>{t('privacy.rights.optOut.title')}</strong> {t('privacy.rights.optOut.description')}</li>
          </ul>
          <p>{t('privacy.rights.contact')}</p>
        </section>

        {/* International Users */}
        <section className={styles.section}>
          <h2>{t('privacy.international.title')}</h2>
          <p>{t('privacy.international.p1')}</p>
          <p>{t('privacy.international.p2')}</p>
        </section>

        {/* Changes to Privacy Policy */}
        <section className={styles.section}>
          <h2>{t('privacy.changes.title')}</h2>
          <p>{t('privacy.changes.description')}</p>
        </section>

        {/* Contact Us */}
        <section className={styles.section}>
          <h2>{t('privacy.contact.title')}</h2>
          <p>{t('privacy.contact.description')}</p>
          <p>
            <strong>{t('privacy.contact.email')}</strong> {t('common.contactEmail')}<br />
            <strong>{t('privacy.contact.company')}</strong> {t('common.companyName')} ({t('common.companyCountry')})
          </p>
        </section>
      </div>
    </div>
  );
}
